import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardList, BookOpen, Timer, Clock, CircleHelp, Star } from 'lucide-react';
import ExamOptions from './SelectSubject';
import { Exam } from '@/components/ExamComponent';
import { LanguageSelect } from '@/components/LanguageSelect';

interface ExamSectionProps {
  onSectionChange?: (section: string) => void;
}

export function ExamSection({ onSectionChange }: ExamSectionProps) {
  const [selectedExamType, setSelectedExamType] = useState<string | null>(null);
  const [showSubjectPopup, setShowSubjectPopup] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en'); // default language

  const examTypes = [
    {
      id: 'full',
      title: 'Full Exam',
      description: 'Complete exam with all subjects covering Gujarat competitive exam syllabus',
      icon: ClipboardList,
      duration: 10800,
      questions: 100,
      marks: 100,
      buttonClass: 'gcep-primary-btn',
      iconBg: 'bg-[hsl(201,91%,60%)] bg-opacity-10',
      iconColor: 'text-[hsl(201,91%,60%)]',
    },
    {
      id: 'subject',
      title: 'Subject Wise',
      description: 'Choose specific subject for focused practice and assessment',
      icon: BookOpen,
      duration: 3600,
      questions: 30,
      marks: 30,
      buttonClass: 'gcep-success-btn',
      iconBg: 'bg-[hsl(142,76%,36%)] bg-opacity-10',
      iconColor: 'text-[hsl(142,76%,36%)]',
    },
    {
      id: 'mock',
      title: 'Mock Test',
      description: 'Timed practice exam simulating real exam conditions',
      icon: Timer,
      duration: 7200,
      questions: 75,
      marks: 75,
      buttonClass: 'gcep-warning-btn',
      iconBg: 'bg-[hsl(45,93%,47%)] bg-opacity-10',
      iconColor: 'text-[hsl(45,93%,47%)]',
    },
  ];

  const handleExamClick = (examType: string) => {
    if (examType === 'full' || examType === 'mock') {
      setSelectedExamType(examType); // ✅ store exam type immediately
      setShowLanguageSelect(true);   // ✅ open language select
    } else if (examType === 'subject') {
      setShowSubjectPopup(true);
    }
  };

  // Called when user selects language and clicks Start Exam
  const handleLanguageConfirm = () => {
    setShowLanguageSelect(false); // ✅ close language select
    // Exam will start automatically because selectedExamType is already set
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Exam Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {examTypes.map((examType) => {
            const Icon = examType.icon;
            return (
              <Card
                key={examType.id}
                className="gcep-card p-8 text-center hover:shadow-2xl transition duration-300 cursor-pointer"
                onClick={() => handleExamClick(examType.id)}
              >
                <CardContent className="p-0">
                  <div className={`${examType.iconBg} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <Icon className={`${examType.iconColor} text-3xl`} size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {examType.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">{examType.description}</p>
                  <div className="space-y-2 mb-6 text-sm text-gray-500 dark:text-gray-400">
                    <p className="flex items-center justify-center">
                      <Clock size={16} className="mr-2" />
                      Duration: {examType.duration / 3600} hours
                    </p>
                    <p className="flex items-center justify-center">
                      <CircleHelp size={16} className="mr-2" />
                      Questions: {examType.questions}
                    </p>
                    <p className="flex items-center justify-center">
                      <Star size={16} className="mr-2" />
                      Marks: {examType.marks}
                    </p>
                  </div>
                  <Button className={`w-full ${examType.buttonClass} py-3 text-lg`}>
                    {examType.id === 'subject' ? 'Select Subject' : `Start ${examType.title}`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* LanguageSelect Modal */}
        {showLanguageSelect && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 w-96">
              <LanguageSelect
                title={selectedExamType === 'full' ? 'Full Exam' : 'Mock Test'}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                onStart={handleLanguageConfirm}
                onClose={() => {
                  setShowLanguageSelect(false);
                  setSelectedExamType(null); 
                }}
              />

            </div>
          </div>
        )}

        {/* Render Exam only after language is selected */}
        {selectedExamType && !showLanguageSelect && (
          <Exam
            key={selectedExamType}
            endpoint="api/examination"
            sessionType={selectedExamType === 'full' ? 'combined' : 'mock'}
            title={selectedExamType === 'full' ? 'Full Exam' : 'Mock Test'}
            language={selectedLanguage}
            duration={selectedExamType === 'full' ? 10800 : 7200}
            limit={selectedExamType === 'full' ? 100 : 75}
            onExit={() => setSelectedExamType(null)}
          />
        )}

        {/* Subject Popup */}
        {showSubjectPopup && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-96">
              <ExamOptions />
              <Button
                onClick={() => setShowSubjectPopup(false)}
                className="mt-4 w-full bg-gray-200 py-2 rounded-lg hover:bg-gray-300"
              >
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Instructions */}
        <Card className="gcep-card mt-8">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Exam Instructions
            </h3>
            <ul className="text-gray-600 dark:text-gray-400 space-y-2">
              <li>• Read each question carefully before selecting your answer</li>
              <li>• You can navigate between questions using the Previous/Next buttons</li>
              <li>• Your progress is automatically saved</li>
              <li>• Make sure you have a stable internet connection</li>
              <li>• Time will be displayed and tracked during the exam</li>
              <li>• Submit your exam before the time limit expires</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
